import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { motion } from 'framer-motion';
import SEOHead, { blogPostSchema } from '@/components/shared/SEOHead';
import { ArrowLeft, CalendarDays, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const { currentTheme: t } = useTheme();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const res = await fetch(`${API}/blog/${slug}`);
      if (res.ok) {
        setPost(await res.json());
      } else {
        // Fallback to mock
        const { BLOG_POSTS } = await import('@/data/mock');
        const found = BLOG_POSTS.find(p => p.slug === slug);
        if (found) setPost(found);
      }
    } catch {
      const { BLOG_POSTS } = await import('@/data/mock');
      const found = BLOG_POSTS.find(p => p.slug === slug);
      if (found) setPost(found);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-28 pb-16 min-h-screen" style={{ background: t.bg }}>
        <div className="max-w-3xl mx-auto px-4 animate-pulse space-y-6">
          <div className="h-8 rounded w-3/4" style={{ background: t.sectionAltBg }} />
          <div className="h-4 rounded w-1/2" style={{ background: t.sectionAltBg }} />
          <div className="aspect-video rounded-xl" style={{ background: t.sectionAltBg }} />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-28 pb-16 min-h-screen text-center" style={{ background: t.bg }}>
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-4" style={{ color: t.text }}>Artículo no encontrado</h1>
          <Link to="/blog" className="text-sm font-medium" style={{ color: t.accent }}>← Volver al blog</Link>
        </div>
      </div>
    );
  }

  return (
    <article style={{ fontFamily: 'Inter, sans-serif' }}>
      <SEOHead
        title={`${post.title} | Blog Motivy`}
        description={post.meta_description || post.excerpt || post.title}
        keywords={post.keywords || ''}
        ogImage={post.og_image || post.image}
        ogType="article"
        structuredData={blogPostSchema(post)}
      />
      <div className="pt-28 lg:pt-36 pb-16 lg:pb-24" style={{ background: t.bg }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium mb-8 transition-colors duration-200" style={{ color: t.accent }}>
            <ArrowLeft size={16} /> Volver al blog
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 text-sm mb-6" style={{ color: t.textMuted }}>
              <span className="flex items-center gap-1.5">
                <CalendarDays size={14} />
                {post.date || (post.published_at && new Date(post.published_at).toLocaleDateString('es-ES', { year:'numeric', month:'long', day:'numeric' }))}
              </span>
              {post.reading_time && (
                <span className="flex items-center gap-1.5">
                  <Clock size={14} /> {post.reading_time} min de lectura
                </span>
              )}
            </div>

            <h1 className="text-3xl lg:text-4xl font-bold mb-6" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>
              {post.title}
            </h1>

            {(post.image || post.og_image) && (
              <img
                src={post.og_image || post.image}
                alt={post.title}
                className="w-full rounded-xl mb-8"
                loading="lazy"
              />
            )}

            <div
              className="prose max-w-none leading-relaxed text-base"
              style={{ color: t.textSecondary }}
              dangerouslySetInnerHTML={{ __html: post.content || `<p>${post.excerpt || ''}</p><p>Este artículo está disponible próximamente con contenido completo.</p>` }}
            />
          </motion.div>
        </div>
      </div>
    </article>
  );
}
